import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Sparkles,
  RefreshCw,
  Activity,
  AlertCircle,
  Ban,
  UserPlus,
  CloudUpload,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fmtDollars = (cents, opts = {}) =>
  ((cents || 0) / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: opts.decimals ?? 0,
  });

const fmtPct = (v) => `${(v * 100).toFixed(1)}%`;
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", { month: "short" });
};

function BulkPublishCdnButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    if (!window.confirm("Push every origin-only asset to Cloudflare Stream? This may take a few minutes and consume Cloudflare bandwidth.")) return;
    setBusy(true);
    try {
      const r = await api.post("/cloudflare-stream/bulk-publish");
      const n = r.data?.queued ?? 0;
      toast.success(n === 0 ? "All assets already on CDN" : `Queued ${n} asset${n === 1 ? "" : "s"} for CDN publish`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk publish failed");
    }
    setBusy(false);
  };
  return (
    <Button
      onClick={run}
      variant="outline"
      disabled={busy}
      data-testid="bulk-cdn-publish-btn"
      className="rounded-full h-10 border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10"
      title="Push every uploaded asset that isn't on Cloudflare Stream yet"
    >
      {busy
        ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Queuing…</>
        : <><CloudUpload className="w-3.5 h-3.5 mr-2" /> Publish all to CDN</>}
    </Button>
  );
}

const StatCard = ({ testId, label, value, sub, tone = "default", Icon }) => {
  const tones = {
    default: { text: "text-foreground", accent: "text-cyan-500 dark:text-cyan-300" },
    positive: { text: "text-emerald-600 dark:text-emerald-400", accent: "text-emerald-500 dark:text-emerald-300" },
    warn: { text: "text-amber-600 dark:text-amber-400", accent: "text-amber-500 dark:text-amber-300" },
    negative: { text: "text-rose-600 dark:text-rose-400", accent: "text-rose-500 dark:text-rose-300" },
  }[tone];
  return (
    <div className="panel-hero rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${tones.accent}`} strokeWidth={1.5} />}
      </div>
      <div className={`mt-2 font-display font-black text-3xl tracking-tighter ${tones.text}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
};

const ActivityIcon = ({ type }) => {
  if (type === "canceled") return <Ban className="w-3.5 h-3.5 text-rose-400" strokeWidth={1.75} />;
  if (type === "past_due") return <AlertCircle className="w-3.5 h-3.5 text-amber-400" strokeWidth={1.75} />;
  return <UserPlus className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.75} />;
};

const ActivityLabel = ({ type }) => {
  if (type === "canceled") return "Canceled";
  if (type === "past_due") return "Past due";
  if (type === "trialing") return "Trial started";
  return "New subscription";
};

export default function RevenueAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/revenue-analytics");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="panel rounded-2xl p-16 flex items-center justify-center text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading revenue analytics…
        </div>
      </div>
    );
  }

  const churnTone = data.churn_rate > 0.05 ? "negative" : data.churn_rate > 0.02 ? "warn" : "positive";
  const convTone = data.trial_conversion_rate < 0.1 ? "warn" : "positive";
  const maxBar = Math.max(1, ...data.mrr_trend.map((m) => m.revenue_cents));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="revenue-analytics-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-500 dark:text-cyan-300">§ Platform revenue</div>
          <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter">Money in motion.</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Live SaaS metrics pulled from Stripe (60 s cache). Super-admin only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BulkPublishCdnButton />
          <Button
            onClick={load}
            variant="outline"
            data-testid="revenue-refresh-btn"
            className="rounded-full h-10 gradient-stroke"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          testId="stat-mrr"
          label="MRR"
          value={fmtDollars(data.mrr_cents)}
          sub={`ARR ~ ${fmtDollars(data.arr_cents)}`}
          Icon={DollarSign}
          tone="positive"
        />
        <StatCard
          testId="stat-active"
          label="Active subs"
          value={data.active_subscribers}
          sub={`${data.trialing_subscribers} trialing · ${data.past_due_subscribers} past due`}
          Icon={Users}
        />
        <StatCard
          testId="stat-conversion"
          label="Trial → paid (90d)"
          value={fmtPct(data.trial_conversion_rate)}
          sub={`${data.paid_conversions_90d} of ${data.trial_signups_90d} signups`}
          Icon={Sparkles}
          tone={convTone}
        />
        <StatCard
          testId="stat-churn"
          label="Churn (30d)"
          value={fmtPct(data.churn_rate)}
          sub={`${data.churned_last_30d} canceled`}
          Icon={churnTone === "positive" ? TrendingDown : TrendingUp}
          tone={churnTone}
        />
      </div>

      {/* MRR trend chart */}
      <div className="panel rounded-2xl p-6" data-testid="mrr-trend-card">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Revenue trend</div>
            <div className="font-display font-bold text-xl">Last 6 months</div>
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            This month: <span className="text-foreground">{fmtDollars(data.revenue_this_month_cents)}</span>
          </div>
        </div>
        <div className="h-[240px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.mrr_trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={fmtMonth}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 100 ? `$${(v / 100).toLocaleString()}` : `$${(v / 100).toFixed(0)}`)}
              />
              <Tooltip
                cursor={{ fill: "hsl(var(--accent) / 0.4)" }}
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontFamily: "var(--font-mono, ui-monospace)",
                  fontSize: 12,
                }}
                formatter={(v) => [fmtDollars(v, { decimals: 2 }), "Revenue"]}
                labelFormatter={(v) => {
                  const [y, m] = v.split("-");
                  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", { year: "numeric", month: "long" });
                }}
              />
              <Bar dataKey="revenue_cents" fill="url(#mrrGrad)" radius={[6, 6, 0, 0]} maxBarSize={64} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {maxBar === 0 && (
          <div className="text-xs text-muted-foreground text-center pt-2 font-mono">
            No paid revenue in the last 6 months yet — first paid checkout will appear here.
          </div>
        )}
      </div>

      {/* Cohort retention */}
      <div className="panel rounded-2xl p-6" data-testid="cohort-retention-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Cohort retention</div>
            <div className="font-display font-bold text-xl">Are subscribers sticking around?</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Rows = signup month · columns = months since signup
          </div>
        </div>
        {(!data.cohorts || data.cohorts.every((c) => c.n_signups === 0)) ? (
          <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl">
            No cohorts yet — retention grid populates from the first paid signup.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="text-left py-2 pr-4 font-normal">Cohort</th>
                  <th className="text-right py-2 pr-4 font-normal">Signups</th>
                  {[0, 1, 2, 3, 4, 5].map((n) => (
                    <th key={n} className="text-center py-2 px-1 font-normal">M{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((c) => (
                  <tr key={c.month} className="border-t border-border" data-testid={`cohort-row-${c.month}`}>
                    <td className="py-2 pr-4 text-foreground">{fmtMonth(c.month)} {c.month.split("-")[0]}</td>
                    <td className="py-2 pr-4 text-right text-foreground">{c.n_signups}</td>
                    {[0, 1, 2, 3, 4, 5].map((n) => {
                      const cell = c.retention[n];
                      if (!cell) return <td key={n} className="py-2 px-1 text-center text-muted-foreground/40">·</td>;
                      const isFuture = cell.pct === 0 && c.n_signups > 0 && n > 0;
                      // Color-scale: 100% = deep emerald, 0% = deep rose, linear interpolation via HSL alpha
                      const alpha = 0.08 + 0.32 * cell.pct;
                      const hue = 15 + 130 * cell.pct;  // 15 (rose) → 145 (emerald)
                      return (
                        <td key={n} className="py-1 px-0.5 text-center">
                          <div
                            className="rounded-md px-2 py-1.5 font-mono"
                            style={{
                              background: c.n_signups === 0 ? "transparent" : `hsl(${hue}, 70%, 50%, ${alpha})`,
                              color: c.n_signups === 0 ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))",
                              border: c.n_signups === 0 ? "1px dashed hsl(var(--border))" : "1px solid transparent",
                            }}
                          >
                            {c.n_signups === 0 ? "—" : isFuture ? "0%" : `${Math.round(cell.pct * 100)}%`}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-3">
          Green = high retention · Red = churn · Grey dash = future month (not yet observed)
        </div>
      </div>

      {/* Activity feed */}
      <div className="panel rounded-2xl p-6" data-testid="revenue-activity-card">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
          <div className="font-display font-bold text-lg">Recent activity</div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground ml-1">last 8 events</span>
        </div>
        {(!data.activity || data.activity.length === 0) ? (
          <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl">
            No subscription activity yet.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.activity.map((e, i) => (
              <div
                key={`${e.customer_id}-${e.at}-${i}`}
                data-testid={`activity-row-${i}`}
                className="py-3 flex items-center justify-between gap-4 flex-wrap hover:bg-accent/30 -mx-3 px-3 rounded-md transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${e.type === "canceled" ? "bg-rose-500/15" : e.type === "past_due" ? "bg-amber-500/15" : "bg-emerald-500/15"}`}>
                    <ActivityIcon type={e.type} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-foreground">
                      <ActivityLabel type={e.type} />{" "}
                      {e.lookup_key && (
                        <span className="text-muted-foreground text-xs">· {e.lookup_key.replace("moxyplay_", "").replace("_", " · ")}</span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                      cus {(e.customer_id || "").slice(-8)} · {new Date(e.at * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-sm text-foreground">
                  {e.amount_cents ? fmtDollars(e.amount_cents, { decimals: 2 }) + " MRR" : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center pt-4">
        {data.cached ? "Served from cache · refreshes every 60s" : `Fetched from Stripe · ${new Date(data.generated_at).toLocaleTimeString()}`}
      </div>
    </div>
  );
}
